import { FC, useState } from 'react';
import { Box, Typography, Collapse, Checkbox, FormControlLabel } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import { PlaceFilters as PlaceFiltersType, PLACE_RATING_OPTIONS } from '../../../../../types/place.types';

import styles from './PlaceFilters.module.scss';

interface PlaceFiltersProps {
  filters: PlaceFiltersType;
  onFiltersChange: (filters: PlaceFiltersType) => void;
}

type FilterSectionKey = 'rating' | 'additionally';

const INITIAL_SECTION_OPEN: Record<FilterSectionKey, boolean> = {
  rating: true,
  additionally: true,
};

const FilterCheckboxUnchecked = () => <span className={styles.filterCheckboxUnchecked} aria-hidden />;

const FilterCheckboxChecked = () => (
  <span className={styles.filterCheckboxChecked} aria-hidden>
    <span className={styles.filterCheckboxCheckmark} />
  </span>
);

const PlaceFilters: FC<PlaceFiltersProps> = ({ filters, onFiltersChange }) => {
  const [openSections, setOpenSections] = useState<Record<FilterSectionKey, boolean>>(INITIAL_SECTION_OPEN);

  const toggleSection = (key: FilterSectionKey) => {
    setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const sectionHeader = (key: FilterSectionKey, title: string) => (
    <Box
      className={`${styles.filterHeader} ${openSections[key] ? styles.filterHeaderExpanded : ''}`}
      onClick={() => toggleSection(key)}
      role="button"
      tabIndex={0}
      aria-expanded={openSections[key]}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggleSection(key);
        }
      }}
    >
      <Typography
        component="span"
        className={styles.filterTitle}
      >
        {title}
      </Typography>
      <KeyboardArrowDownIcon
        className={`${styles.expandIcon} ${openSections[key] ? styles.expandIconOpen : ''}`}
        aria-hidden
      />
    </Box>
  );

  const handleRatingToggle = (rating: number) => {
    const next = filters.ratings.includes(rating)
      ? filters.ratings.filter(r => r !== rating)
      : [...filters.ratings, rating];
    onFiltersChange({
      ...filters,
      ratings: next,
    });
  };

  const setFlag = (key: 'petFriendly' | 'goodForChildren' | 'goodForGroup', checked: boolean) => {
    onFiltersChange({
      ...filters,
      [key]: checked,
    });
  };

  return (
    <Box className={styles.filtersContainer}>
      <Box className={styles.filterSection}>
        {sectionHeader('rating', 'Rating')}
        <Collapse
          in={openSections.rating}
          timeout={280}
          unmountOnExit={false}
        >
          <Box className={styles.checkboxGroup}>
            {PLACE_RATING_OPTIONS.map(option => (
              <FormControlLabel
                key={option.value}
                className={styles.checkboxRow}
                control={
                  <Checkbox
                    disableRipple
                    checked={filters.ratings.includes(option.value)}
                    onChange={() => handleRatingToggle(option.value)}
                    className={styles.filterCheckbox}
                    size="small"
                    icon={<FilterCheckboxUnchecked />}
                    checkedIcon={<FilterCheckboxChecked />}
                  />
                }
                label={<span className={styles.checkboxLabel}>{option.label}</span>}
              />
            ))}
          </Box>
        </Collapse>
      </Box>

      <Box className={styles.filterSection}>
        {sectionHeader('additionally', 'Additionally')}
        <Collapse
          in={openSections.additionally}
          timeout={280}
          unmountOnExit={false}
        >
          <Box className={styles.checkboxGroup}>
            <FormControlLabel
              className={styles.checkboxRow}
              control={
                <Checkbox
                  disableRipple
                  checked={filters.petFriendly}
                  onChange={e => setFlag('petFriendly', e.target.checked)}
                  className={styles.filterCheckbox}
                  size="small"
                  icon={<FilterCheckboxUnchecked />}
                  checkedIcon={<FilterCheckboxChecked />}
                />
              }
              label={<span className={styles.checkboxLabel}>Pet-friendly</span>}
            />
            <FormControlLabel
              className={styles.checkboxRow}
              control={
                <Checkbox
                  disableRipple
                  checked={filters.goodForGroup}
                  onChange={e => setFlag('goodForGroup', e.target.checked)}
                  className={styles.filterCheckbox}
                  size="small"
                  icon={<FilterCheckboxUnchecked />}
                  checkedIcon={<FilterCheckboxChecked />}
                />
              }
              label={<span className={styles.checkboxLabel}>Good for groups</span>}
            />
            <FormControlLabel
              className={styles.checkboxRow}
              control={
                <Checkbox
                  disableRipple
                  checked={filters.goodForChildren}
                  onChange={e => setFlag('goodForChildren', e.target.checked)}
                  className={styles.filterCheckbox}
                  size="small"
                  icon={<FilterCheckboxUnchecked />}
                  checkedIcon={<FilterCheckboxChecked />}
                />
              }
              label={<span className={styles.checkboxLabel}>Good for children</span>}
            />
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
};

export default PlaceFilters;
